import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import VendorLayout from "../../components/VendorLayout";
import { api, formatEUR, formatDateFR, formatApiError, photoUrl, API } from "../../lib/api";
import { toast } from "sonner";
import { confirmAction } from "../../components/ConfirmDialog";
import {
  ArrowLeft, Pencil, Loader2, MapPin, Hash, Car, Fuel, Gauge, Calendar,
  Upload, Trash2, Download, FileText, AlertTriangle, TrendingUp, Clock,
  Wrench, Users, Zap, Settings2, CircleDollarSign, ShieldCheck, ExternalLink,
  Archive, RotateCcw, ArrowLeftRight,
} from "lucide-react";
import { MaintenanceTab, IssuesTab } from "../../components/pilotage/VehicleOpsTabs";
import { orderedActiveFields } from "../../components/fleet/fieldConfig";
import { vehiclePrimaryLabel } from "../../lib/vehicleLabel";
import GlobalFines from "../../components/pilotage/FinesView";
import { GlobalClaims } from "../../components/pilotage/FleetEventsView";

const DOC_TYPES = [
  { value: "registration", label: "Carte grise" },
  { value: "insurance", label: "Assurance" },
  { value: "technical_inspection", label: "Contrôle technique" },
  { value: "purchase_invoice", label: "Facture d'achat" },
  { value: "maintenance", label: "Entretien / réparation" },
  { value: "other", label: "Autre" },
];

const STATUS_COLOR_CLASSES = {
  emerald: "bg-emerald-100 text-emerald-700 border-emerald-200",
  violet: "bg-violet-100 text-violet-700 border-violet-200",
  blue: "bg-blue-100 text-blue-700 border-blue-200",
  sky: "bg-sky-100 text-sky-700 border-sky-200",
  amber: "bg-amber-100 text-amber-700 border-amber-200",
  red: "bg-red-100 text-red-700 border-red-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  rose: "bg-rose-100 text-rose-700 border-rose-200",
  teal: "bg-teal-100 text-teal-700 border-teal-200",
};

const DEFAULT_STATUS_MAP = {
  active: { label: "Disponible", color: "emerald" },
  rented: { label: "Loué", color: "violet" },
  reserved: { label: "Réservé", color: "blue" },
  cleaning: { label: "Nettoyage", color: "sky" },
  maintenance: { label: "Maintenance", color: "amber" },
  out_of_service: { label: "Hors service", color: "red" },
  sold: { label: "Vendu", color: "slate" },
  inactive: { label: "Inactif", color: "slate" },
};

const STATUS_LABELS = Object.fromEntries(
  Object.entries(DEFAULT_STATUS_MAP).map(([k, v]) => [k, { label: v.label, color: STATUS_COLOR_CLASSES[v.color] }])
);

const BOOKING_STATUS = {
  confirmed: { label: "Confirmée", color: "bg-emerald-100 text-emerald-700" },
  pending: { label: "En attente", color: "bg-amber-100 text-amber-700" },
  completed: { label: "Terminée", color: "bg-slate-100 text-slate-600" },
  cancelled: { label: "Annulée", color: "bg-red-100 text-red-700" },
};

export default function VehicleDetailVendor({ embedded = false, embeddedId = null }) {
  const params = useParams();
  const id = embeddedId || params.id;
  const navigate = useNavigate();
  const Wrap = embedded ? EmbedWrap : VendorLayout;
  const [loading, setLoading] = useState(true);
  const [vehicle, setVehicle] = useState(null);
  const [location, setLocation] = useState(null);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState({ bookings: [], tasks: [] });
  const [documents, setDocuments] = useState([]);
  const [events, setEvents] = useState([]);
  const [statuses, setStatuses] = useState(null);
  const [typeConfig, setTypeConfig] = useState(null);
  const [typeLabel, setTypeLabel] = useState("");
  const [categoryLabel, setCategoryLabel] = useState("");
  const [fieldCatalog, setFieldCatalog] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const vehicleRef = React.useMemo(() => vehicle ? [{ id: vehicle.id, name: vehicle.name }] : [], [vehicle]);
  const [activeTab, setActiveTab] = useState(() => new URLSearchParams(window.location.search).get("vtab") || "overview");

  const load = async () => {
    setLoading(true);
    try {
      const [d, s, h, docs, ev, settings] = await Promise.all([
        api.get(`/vendor/vehicles/${id}`),
        api.get(`/vendor/vehicles/${id}/stats`),
        api.get(`/vendor/vehicles/${id}/history`),
        api.get(`/vendor/vehicles/${id}/documents`),
        api.get(`/vendor/vehicles/${id}/events`),
        api.get(`/vendor/settings`).catch(() => ({ data: {} })),
      ]);
      setVehicle(d.data.vehicle);
      setLocation(d.data.location);
      setStats(s.data);
      setHistory(h.data);
      setDocuments(docs.data.documents || []);
      setEvents(ev.data.events || []);
      const fleet = settings.data?.fleet || {};
      if (Array.isArray(fleet.statuses) && fleet.statuses.length > 0) {
        setStatuses(fleet.statuses);
      }
      setFieldCatalog(settings.data?.catalog?.field_catalog || []);
      api.get("/vendor/locations").then((r) => setAllLocations(r.data.locations || [])).catch(() => {});
      const vtypes = Array.isArray(fleet.vehicle_types) ? fleet.vehicle_types : [];
      setTypeConfig(vtypes.find((t) => t.value === d.data.vehicle?.vehicle_type)?.fields || null);
      setTypeLabel(vtypes.find((t) => t.value === d.data.vehicle?.vehicle_type)?.label || "");
      setCategoryLabel((Array.isArray(fleet.categories) ? fleet.categories : []).find((c) => c.value === d.data.vehicle?.category)?.label || "");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossible de charger la fiche");
      if (err.response?.status === 404) navigate("/vendor/pilotage?tab=vehicles");
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (loading) {
    return (
      <Wrap>
        <div className="w-full p-8 flex items-center justify-center py-32">
          <Loader2 className="h-6 w-6 animate-spin text-violet-600" />
        </div>
      </Wrap>
    );
  }
  if (!vehicle) return null;

  const status = (() => {
    if (statuses) {
      const s = statuses.find((x) => x.value === vehicle.status);
      if (s) return { label: s.label, color: STATUS_COLOR_CLASSES[s.color] || STATUS_COLOR_CLASSES.slate };
    }
    return STATUS_LABELS[vehicle.status] || STATUS_LABELS.active;
  })();
  const photos = vehicle.photos?.length ? vehicle.photos : [];

  return (
    <Wrap>
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
          <Link to="/vendor/pilotage?tab=vehicles" className="inline-flex items-center gap-1 hover:text-slate-900" data-testid="back-to-vehicles">
            <ArrowLeft className="h-4 w-4" /> Retour aux véhicules
          </Link>
        </div>

        {/* Header */}
        <VehicleHeader
          vehicle={vehicle}
          status={status}
          location={location}
          categoryLabel={categoryLabel}
          onEdit={() => navigate(`/vendor/pilotage?tab=vehicles&edit=${vehicle.id}`)}
          onArchive={async () => {
            const verb = vehicle.archived ? "Restaurer" : "Archiver";
            if (!(await confirmAction({
              title: `${verb} « ${vehicle.name} » ?`,
              message: vehicle.archived
                ? "Le véhicule sera restauré dans votre flotte active et pourra de nouveau être réservé."
                : "Le véhicule sera retiré de la flotte active : il ne pourra plus être réservé, mais son historique est conservé.",
              confirmLabel: `Confirmer : ${verb.toLowerCase()}`,
              variant: "warning",
            }))) return;
            try {
              const { data } = await api.post(`/vendor/vehicles/${vehicle.id}/archive`);
              toast.success(data.archived ? "Véhicule archivé" : "Véhicule restauré");
              setVehicle({ ...vehicle, archived: data.archived });
            } catch (err) {
              toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
            }
          }}
          onDelete={async () => {
            if (!(await confirmAction({
              title: "Supprimer définitivement ce véhicule ?",
              message: `« ${vehicle.name} » sera définitivement supprimé de votre flotte, ainsi que ses photos et son paramétrage. Cette action est IRRÉVERSIBLE.`,
              confirmLabel: "Supprimer le véhicule",
              variant: "danger",
              requireText: "SUPPRIMER",
            }))) return;
            try {
              await api.delete(`/vendor/vehicles/${vehicle.id}`);
              toast.success("Véhicule supprimé");
              navigate("/vendor/pilotage?tab=vehicles");
            } catch (err) {
              if (err.response?.status === 409) {
                if (await confirmAction({
                  title: "Suppression impossible — archiver à la place ?",
                  message: "Ce véhicule a un historique de réservations et ne peut pas être supprimé. L'archiver le retirera de la flotte active sans perdre l'historique.",
                  confirmLabel: "Archiver le véhicule",
                  variant: "warning",
                })) {
                  const { data } = await api.post(`/vendor/vehicles/${vehicle.id}/archive`);
                  toast.success(data.archived ? "Véhicule archivé" : "Véhicule restauré");
                  setVehicle({ ...vehicle, archived: data.archived });
                }
              } else {
                toast.error(err.response?.data?.detail || "Suppression impossible");
              }
            }
          }}
        />

        {/* Photos */}
        {photos.length > 0 && (
          <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="col-span-2 md:col-span-2 md:row-span-2 aspect-[16/10] md:aspect-auto rounded-2xl overflow-hidden bg-slate-100">
              <img src={photoUrl(photos[0])} alt="" className="h-full w-full object-cover" />
            </div>
            {photos.slice(1, 5).map((p, i) => (
              <div key={p} className="aspect-square rounded-2xl overflow-hidden bg-slate-100">
                <img src={photoUrl(p)} alt="" className="h-full w-full object-cover" />
              </div>
            ))}
          </div>
        )}

        {/* KPI bar */}
        <StatsBar stats={stats} vehicle={vehicle} />

        {/* Tabs */}
        <div className="mt-8 border-b border-slate-200 flex items-center gap-1 overflow-x-auto">
          {[
            ["overview", "Vue d'ensemble"],
            ["specs", "Caractéristiques"],
            ["maintenance", "Entretien"],
            ["issues", "Signalement"],
            ["fines", "Contravention"],
            ["claims", "Sinistre"],
            ["finance", "Finance"],
            ["history", "Historique"],
            ["documents", `Documents (${documents.length})`],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setActiveTab(k)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === k ? "border-violet-600 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"}`}
              data-testid={`tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="mt-6">
          {activeTab === "overview" && <OverviewTab vehicle={vehicle} location={location} allLocations={allLocations} onTransferred={load} typeConfig={typeConfig} fieldCatalog={fieldCatalog} typeLabel={typeLabel} categoryLabel={categoryLabel} statusLabel={status.label} />}
          {activeTab === "specs" && <SpecsTab vehicle={vehicle} typeConfig={typeConfig} fieldCatalog={fieldCatalog} />}
          {activeTab === "maintenance" && <MaintenanceTab vehicleId={vehicle.id} />}
          {activeTab === "issues" && <IssuesTab vehicleId={vehicle.id} />}
          {activeTab === "fines" && <GlobalFines navigate={navigate} vehicles={vehicleRef} fixedVehicleId={vehicle.id} />}
          {activeTab === "claims" && <GlobalClaims navigate={navigate} vehicles={vehicleRef} fixedVehicleId={vehicle.id} />}
          {activeTab === "finance" && <FinanceTab vehicle={vehicle} stats={stats} />}
          {activeTab === "history" && <HistoryTab history={history} events={events} />}
          {activeTab === "documents" && (
            <DocumentsTab
              vehicleId={vehicle.id}
              documents={documents}
              onChange={setDocuments}
            />
          )}
        </div>
      </div>
    </Wrap>
  );
}

// ===== Header =====
function VehicleHeader({ vehicle, status, location, categoryLabel, onEdit, onDelete, onArchive }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500 mb-2">
          {categoryLabel && <span className="uppercase tracking-wider font-semibold text-slate-500" data-testid="vehicle-category">{categoryLabel}</span>}
          {vehicle.year && <span>· {vehicle.year}</span>}
          {vehicle.color && <span>· {vehicle.color}</span>}
        </div>
        <div className="text-[10px] tracking-[0.25em] uppercase font-bold text-violet-500">Fiche véhicule</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 mt-1" data-testid="vehicle-title">
          {vehiclePrimaryLabel(vehicle)}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-xs font-semibold ${status.color}`} data-testid="vehicle-status">
            <span className="h-1.5 w-1.5 rounded-full bg-current" /> {status.label}
          </span>
          {vehicle.archived && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-slate-800 text-white text-xs font-semibold" data-testid="vehicle-archived-badge">
              <Archive className="h-3 w-3" /> Archivé
            </span>
          )}
          {vehicle.license_plate && (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-slate-100 text-slate-900 text-xs font-mono" data-testid="vehicle-plate">
              <Hash className="h-3 w-3" /> {vehicle.license_plate}
            </span>
          )}
          {vehicle.city && (
            <span className="inline-flex items-center gap-1 text-slate-600 text-xs">
              <MapPin className="h-3.5 w-3.5" /> {vehicle.city}
              {location?.name ? ` · ${location.name}` : ""}
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0 self-start" data-testid="fiche-actions">
        <button
          onClick={onEdit}
          className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
          data-testid="edit-vehicle-btn"
        >
          <Pencil className="h-3.5 w-3.5" /> Modifier
        </button>
        <button
          onClick={onArchive}
          className="h-10 px-4 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
          data-testid="archive-vehicle-btn"
        >
          {vehicle.archived ? <RotateCcw className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />} {vehicle.archived ? "Restaurer" : "Archiver"}
        </button>
        <button
          onClick={onDelete}
          className="h-10 px-4 rounded-xl border border-red-200 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors inline-flex items-center gap-2 whitespace-nowrap"
          data-testid="delete-vehicle-btn"
        >
          <Trash2 className="h-3.5 w-3.5" /> Supprimer
        </button>
      </div>
    </div>
  );
}

// ===== Stats bar =====
function StatsBar({ stats, vehicle }) {
  const items = [
    { icon: TrendingUp, label: "Revenu net", value: formatEUR(stats?.total_net || 0), testid: "kpi-net" },
    { icon: Gauge, label: "Occupation 90j", value: `${stats?.occupation_rate_90d ?? 0}%`, testid: "kpi-occ" },
    { icon: Users, label: "Locations", value: stats?.bookings_count || 0, testid: "kpi-count" },
    { icon: CircleDollarSign, label: "€ / jour loué", value: formatEUR(stats?.revenue_per_day || 0), testid: "kpi-perday" },
    { icon: Wrench, label: "Charge / mois", value: formatEUR(stats?.monthly_costs || 0), testid: "kpi-costs" },
    { icon: Clock, label: "Dernière loc.", value: stats?.last_booking ? formatDateFR(stats.last_booking) : "—", testid: "kpi-last" },
  ];
  return (
    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
      {items.map((it) => {
        const Icon = it.icon;
        return (
          <div key={it.label} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={it.testid}>
            <div className="flex items-center justify-between">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-400">{it.label}</span>
              <Icon className="h-3.5 w-3.5 text-slate-400" />
            </div>
            <div className="mt-1 font-heading text-xl font-semibold text-slate-900">{it.value}</div>
          </div>
        );
      })}
    </div>
  );
}

// ===== Tabs content =====
function MultiAgencyCard({ vehicle, allLocations, onTransferred }) {
  const [open, setOpen] = useState(false);
  const [dest, setDest] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const nameOf = (id) => allLocations.find((l) => l.id === id)?.name || "—";
  const attach = (vehicle.location_ids && vehicle.location_ids.length) ? vehicle.location_ids : (vehicle.location_id ? [vehicle.location_id] : []);
  const physical = vehicle.current_location_id || vehicle.location_id;
  const transfer = async () => {
    if (!dest) { toast.error("Choisissez une agence de destination."); return; }
    setSaving(true);
    try {
      await api.post(`/vendor/vehicles/${vehicle.id}/transfer`, { to_location_id: dest, note });
      toast.success("Transfert enregistré");
      setOpen(false); setDest(""); setNote("");
      onTransferred && onTransferred();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
    }
    setSaving(false);
  };
  return (
    <Card title="Agences & localisation">
      <div data-testid="veh-agencies-card">
        <div className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Agences de rattachement</div>
        <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="veh-attachment-agencies">
          {attach.length === 0 ? <span className="text-sm text-slate-400">—</span> : attach.map((id) => (
            <span key={id} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-semibold">{nameOf(id)}</span>
          ))}
        </div>
        <div className="mt-3 text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Localisation physique actuelle</div>
        <div className="mt-1 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900" data-testid="veh-physical-location">
            <MapPin className="h-3.5 w-3.5 text-violet-600" /> {nameOf(physical)}
            {vehicle.departure_locked && <span className="px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[9px] font-bold uppercase" title="Prochain départ restreint à cette agence">verrouillé</span>}
          </span>
          {allLocations.length > 1 && (
            <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1 px-2.5 h-8 rounded-lg border border-slate-200 text-xs font-semibold text-slate-600 hover:border-violet-400 hover:text-violet-700" data-testid="veh-transfer-btn">
              <ArrowLeftRight className="h-3.5 w-3.5" /> Transférer
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="veh-transfer-modal">
          <div className="fixed inset-0 bg-violet-950/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative w-full max-w-sm rounded-3xl bg-white shadow-2xl p-6">
            <h3 className="font-heading text-lg font-semibold text-slate-900">Transférer le véhicule</h3>
            <p className="text-xs text-slate-500 mt-1">La localisation physique sera mise à jour. Les agences de rattachement restent inchangées.</p>
            <div className="mt-4 space-y-3">
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Agence de destination</div>
                <select value={dest} onChange={(e) => setDest(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm bg-white" data-testid="veh-transfer-dest">
                  <option value="">— Choisir —</option>
                  {allLocations.filter((l) => l.id !== physical).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1.5">Commentaire (facultatif)</div>
                <input value={note} onChange={(e) => setNote(e.target.value)} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" data-testid="veh-transfer-note" />
              </div>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setOpen(false)} className="px-4 h-10 rounded-xl border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50" data-testid="veh-transfer-cancel">Annuler</button>
              <button onClick={transfer} disabled={saving} className="px-5 h-10 rounded-xl bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 disabled:opacity-50" data-testid="veh-transfer-confirm">Confirmer le transfert</button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

function OverviewTab({ vehicle, location, allLocations = [], onTransferred, typeConfig, fieldCatalog = [], typeLabel, categoryLabel, statusLabel }) {
  const hasConfig = typeConfig && (typeConfig.catalog || typeConfig.custom || typeConfig.order);
  const EXCLUDE = new Set(["name", "description", "internal_notes", "known_damages"]);
  const rawVal = (f) => f.kind === "custom"
    ? vehicle.custom_fields?.[f.id]
    : (f.key?.startsWith("equip.") ? vehicle.equipment?.[f.key.slice(6)] : vehicle[f.key]);
  const fmt = (f, val) => {
    if (val == null || val === "" || val === false) return null;
    if (f.input === "images" || f.input === "files") return null;
    if (f.input === "checkbox") return "Oui";
    if (f.input === "date") return formatDateFR(val);
    if (Array.isArray(val)) return val.length ? val.join(", ") : null;
    return String(val);
  };
  const specRows = hasConfig
    ? orderedActiveFields(typeConfig, fieldCatalog)
        .filter((f) => !(f.kind === "catalog" && EXCLUDE.has(f.key)))
        .map((f) => ({ label: f.label, value: fmt(f, rawVal(f)) }))
        .filter((r) => r.value != null)
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <MultiAgencyCard vehicle={vehicle} allLocations={allLocations} onTransferred={onTransferred} />
      <Card title="Informations générales">
        <Row label="Type de bien" value={typeLabel || "—"} />
        <Row label="Catégorie" value={categoryLabel || "—"} />
        <Row label="N° d'identification" value={vehicle.internal_ref || vehicle.license_plate || "—"} mono />
      </Card>
      <Card title="Publication">
        <Row label="Statut" value={statusLabel || vehicle.status || "—"} />
        <Row label="Ville" value={vehicle.city || "—"} />
        {vehicle.next_available_date && <Row label="Prochaine dispo." value={formatDateFR(vehicle.next_available_date)} />}
        {location?.address && <Row label="Adresse agence" value={location.address} />}
      </Card>
      {hasConfig ? (
        <Card title="Caractéristiques du bien">
          {specRows.length === 0 ? (
            <p className="text-sm text-slate-500 italic">Aucune caractéristique renseignée.</p>
          ) : (
            specRows.map((r, i) => <Row key={i} label={r.label} value={r.value} />)
          )}
        </Card>
      ) : (
        <Card title="Résumé">
          <Row label="Marque" value={vehicle.brand || "—"} />
          <Row label="Modèle" value={vehicle.model || "—"} />
          <Row label="Année" value={vehicle.year || "—"} />
          <Row label="Couleur" value={vehicle.color || "—"} />
          <Row label="Places" value={vehicle.seats || "—"} />
          <Row label="Carburant" value={vehicle.fuel || "—"} />
        </Card>
      )}
      {(vehicle.description || vehicle.internal_notes || vehicle.known_damages) && (
        <Card title="Notes" className="lg:col-span-3">
          {vehicle.description && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Description publique</div>
              <p className="text-sm text-slate-700 whitespace-pre-line">{vehicle.description}</p>
            </div>
          )}
          {vehicle.known_damages && (
            <div className="mb-3">
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Dommages connus</div>
              <p className="text-sm text-slate-700 whitespace-pre-line">{vehicle.known_damages}</p>
            </div>
          )}
          {vehicle.internal_notes && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Notes internes</div>
              <p className="text-sm text-slate-700 whitespace-pre-line">{vehicle.internal_notes}</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

function SpecsTab({ vehicle, typeConfig, fieldCatalog = [] }) {
  const hasConfig = typeConfig && (typeConfig.catalog || typeConfig.custom || typeConfig.order);
  if (hasConfig) return <DynamicSpecs vehicle={vehicle} typeConfig={typeConfig} fieldCatalog={fieldCatalog} />;
  // Fallback legacy (biens sans configuration de champs → comportement actuel)
  const equipment = vehicle.equipment || {};
  const equipList = Object.entries(equipment).filter(([, v]) => v);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Motorisation">
        <Row label="Carburant" value={vehicle.fuel} icon={Fuel} />
        <Row label="Boîte de vitesse" value={vehicle.transmission} icon={Settings2} />
        <Row label="Puissance fiscale" value={vehicle.fiscal_power ? `${vehicle.fiscal_power} CV` : "—"} />
        <Row label="Puissance moteur" value={vehicle.engine_power ? `${vehicle.engine_power} ch` : "—"} />
        <Row label="Capacité réservoir" value={vehicle.tank_capacity ? `${vehicle.tank_capacity} L` : "—"} />
        <Row label="Autonomie" value={vehicle.autonomy ? `${vehicle.autonomy} km` : "—"} />
      </Card>
      <Card title="Usage & environnement">
        <Row label="Kilométrage actuel" value={vehicle.mileage != null ? `${Number(vehicle.mileage).toLocaleString("fr-FR")} km` : "—"} icon={Gauge} />
        <Row label="Km max autorisé" value={vehicle.max_mileage ? `${Number(vehicle.max_mileage).toLocaleString("fr-FR")} km` : "—"} />
        <Row label="Niveau carburant" value={vehicle.fuel_level != null ? `${vehicle.fuel_level}%` : "—"} />
        <Row label="Mise en circulation" value={vehicle.first_registration_date ? formatDateFR(vehicle.first_registration_date) : "—"} icon={Calendar} />
        <Row label="Émissions CO₂" value={vehicle.co2_emissions ? `${vehicle.co2_emissions} g/km` : "—"} />
        <Row label="Crit'Air" value={vehicle.crit_air ? `Crit'Air ${vehicle.crit_air}` : "—"} />
      </Card>
      <Card title="État du véhicule">
        <Row label="État intérieur" value={condLabel(vehicle.condition_interior)} />
        <Row label="État extérieur" value={condLabel(vehicle.condition_exterior)} />
      </Card>
      <Card title="Équipements">
        {equipList.length === 0 ? (
          <p className="text-sm text-slate-500 italic">Aucun équipement renseigné.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {equipList.map(([k]) => (
              <span key={k} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-violet-50 text-violet-700 text-xs font-medium border border-violet-100">
                <ShieldCheck className="h-3 w-3" /> {equipLabel(k)}
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// Fiche pilotée par la configuration des champs du Type (libellés + ordre + champs perso)
function DynamicSpecs({ vehicle, typeConfig, fieldCatalog }) {
  // Marque/Modèle sont affichés dans l'en-tête (libellé principal) : on les retire ici.
  const fields = orderedActiveFields(typeConfig, fieldCatalog)
    .filter((f) => !(f.kind === "catalog" && (f.key === "brand" || f.key === "model")));
  const rawVal = (f) => f.kind === "custom"
    ? vehicle.custom_fields?.[f.id]
    : (f.key.startsWith("equip.") ? vehicle.equipment?.[f.key.slice(6)] : vehicle[f.key]);
  const isAsset = (f) => f.input === "images" || f.input === "files";
  const assetFields = fields.filter(isAsset);
  const rowFields = fields.filter((f) => !isAsset(f));

  const display = (f) => {
    const v = rawVal(f);
    if (v == null || v === "") return "—";
    if (f.input === "checkbox" || typeof v === "boolean") return v ? "Oui" : "Non";
    if (Array.isArray(v)) return v.length ? v.join(", ") : "—";
    if (f.input === "date") { try { return formatDateFR(v); } catch { return v; } }
    return String(v);
  };

  if (!fields.length) return <p className="text-sm text-slate-500 italic">Aucun champ actif pour ce type de bien.</p>;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="specs-dynamic">
      <Card title="Caractéristiques du bien">
        {rowFields.length === 0 ? <p className="text-sm text-slate-500 italic">Aucune caractéristique renseignée.</p>
          : rowFields.map((f) => <Row key={f.token} label={f.label} value={display(f)} />)}
      </Card>
      {assetFields.map((f) => {
        const assets = Array.isArray(rawVal(f)) ? rawVal(f) : [];
        return (
          <Card key={f.token} title={f.label}>
            {assets.length === 0 ? <p className="text-sm text-slate-500 italic">Aucun élément.</p> : (
              <div className="flex flex-wrap gap-2" data-testid={`specs-custom-${f.id}`}>
                {assets.map((a) => f.input === "images"
                  ? <img key={a.id} src={photoUrl(a.path)} alt={a.filename} className="h-20 w-20 rounded-lg object-cover border border-slate-200" />
                  : <a key={a.id} href={photoUrl(a.path)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-slate-200 bg-white text-xs text-slate-600 hover:border-violet-300 max-w-[200px]"><FileText className="h-3.5 w-3.5 shrink-0" /><span className="truncate">{a.filename}</span></a>
                )}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function FinanceTab({ vehicle, stats }) {
  const costs = stats?.costs || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card title="Tarification">
        <Row label="Tarifs de location" value="Grille tarifaire (Paramétrages → Tarification)" />
        <Row label="Caution" value={vehicle.deposit ? formatEUR(vehicle.deposit) : "—"} />
        <Row label="Franchise" value={vehicle.deductible ? formatEUR(vehicle.deductible) : "—"} />
        <Row label="Km inclus / jour" value={vehicle.km_included_daily || "—"} />
        <Row label="Prix km supp." value={vehicle.price_extra_km ? formatEUR(vehicle.price_extra_km) : "—"} />
      </Card>
      <Card title="Coûts d'exploitation">
        <Row label="Prix d'achat" value={vehicle.purchase_price ? formatEUR(vehicle.purchase_price) : "—"} />
        <Row label="Date d'achat" value={vehicle.purchase_date ? formatDateFR(vehicle.purchase_date) : "—"} />
        <Row label="Valeur estimée" value={vehicle.estimated_value ? formatEUR(vehicle.estimated_value) : "—"} />
        <Row label="Assurance / mois" value={vehicle.insurance_monthly ? formatEUR(vehicle.insurance_monthly) : "—"} />
        <Row label="Entretien estimé / an" value={vehicle.maintenance_estimated ? formatEUR(vehicle.maintenance_estimated) : "—"} />
        <div className="mt-3 pt-3 border-t border-slate-100">
          <Row label="Charges mensuelles (Paramétrages)" value={formatEUR(stats?.monthly_costs || 0)} />
        </div>
      </Card>
      <Card title="Rentabilité (période observée)" className="lg:col-span-2">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <Metric label="Revenu brut" value={formatEUR(stats?.total_revenue || 0)} />
          <Metric label="Revenu net" value={formatEUR(stats?.total_net || 0)} accent />
          <Metric label="Jours loués" value={stats?.total_days_rented || 0} />
        </div>
        {(costs.purchase_price || stats?.monthly_costs) && (
          <p className="mt-3 text-xs text-slate-500">
            <AlertTriangle className="inline h-3 w-3 mr-1 text-slate-400" />
            Marge nette réelle = revenu net − (charges × mois écoulés). Ajustez les coûts dans <Link to="/vendor/parametrages?section=vehicles" className="underline hover:text-violet-600">Paramétrages → Coûts de revient</Link>.
          </p>
        )}
      </Card>
    </div>
  );
}

function HistoryTab({ history, events }) {
  const { bookings, tasks } = history;
  const eventLabels = {
    created: "Création",
    updated: "Modification",
    deleted: "Suppression",
    costs_updated: "Coûts",
    document_added: "Doc ajouté",
    document_removed: "Doc supprimé",
  };
  const eventColors = {
    created: "bg-emerald-100 text-emerald-700",
    updated: "bg-sky-100 text-sky-700",
    deleted: "bg-red-100 text-red-700",
    costs_updated: "bg-amber-100 text-amber-700",
    document_added: "bg-violet-100 text-violet-700",
    document_removed: "bg-slate-100 text-slate-700",
  };
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card title={`Réservations (${bookings.length})`}>
        {bookings.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-8 text-center">Aucune réservation.</p>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {bookings.map((b) => {
              const st = BOOKING_STATUS[b.status] || BOOKING_STATUS.confirmed;
              return (
                <Link
                  key={b.id}
                  to={`/vendor/bookings/${b.id}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-300 hover:bg-slate-50 transition-colors"
                  data-testid={`history-booking-${b.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 truncate">{b.client_name}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {formatDateFR(b.start_date)} → {formatDateFR(b.end_date)} · {b.days}j
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-semibold text-slate-900">{formatEUR(b.total || 0)}</div>
                    <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-[10px] font-semibold ${st.color}`}>{st.label}</span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Card>
      <Card title={`Tâches (${tasks.length})`}>
        {tasks.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-8 text-center">Aucune tâche.</p>
        ) : (
          <div className="space-y-2 max-h-[500px] overflow-y-auto">
            {tasks.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100" data-testid={`history-task-${t.id}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-900 truncate">{t.title || t.custom_label || t.type}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {t.date ? formatDateFR(t.date) : "Sans date"}{t.start_time ? ` · ${t.start_time}` : ""}
                  </div>
                </div>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                  t.status === "done" ? "bg-emerald-100 text-emerald-700" :
                  t.status === "in_progress" ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                }`}>
                  {t.status === "done" ? "Terminée" : t.status === "in_progress" ? "En cours" : "À faire"}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>
      <Card title={`Événements (${events.length})`}>
        {events.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-8 text-center">Aucun événement enregistré.</p>
        ) : (
          <div className="relative max-h-[500px] overflow-y-auto pl-3" data-testid="events-timeline">
            <span className="absolute left-1 top-2 bottom-2 w-px bg-slate-200" aria-hidden />
            <div className="space-y-3">
              {events.map((e) => (
                <div key={e.id} className="relative" data-testid={`event-${e.id}`}>
                  <span className="absolute -left-2 top-1.5 h-2 w-2 rounded-full bg-violet-500 ring-2 ring-white" />
                  <div className="pl-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-semibold ${eventColors[e.type] || "bg-slate-100 text-slate-700"}`}>
                        {eventLabels[e.type] || e.type}
                      </span>
                      <span className="text-[10px] text-slate-400">{formatDateFR(e.created_at?.slice(0, 10))} · {e.created_at?.slice(11, 16)}</span>
                    </div>
                    <div className="text-sm text-slate-800 mt-1">{e.description}</div>
                    {e.actor_name && <div className="text-[10px] text-slate-400 mt-0.5">par {e.actor_name}</div>}
                    {e.changes && e.type === "updated" && (
                      <div className="mt-1.5 text-[10px] text-slate-500 space-y-0.5">
                        {Object.entries(e.changes).slice(0, 4).map(([k, v]) => (
                          <div key={k}>
                            <span className="font-semibold">{k}</span> :{" "}
                            <span className="line-through text-slate-400">{String(v.before ?? "—").slice(0, 40)}</span>
                            {" → "}
                            <span className="text-slate-700">{String(v.after ?? "—").slice(0, 40)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ===== Documents =====
function DocumentsTab({ vehicleId, documents, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("registration");
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("");

  const upload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    try {
      let next = documents;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const params = { doc_type: docType };
        if (label) params.label = label;
        if (expiry) params.expiry_date = expiry;
        const { data } = await api.post(`/vendor/vehicles/${vehicleId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          params,
        });
        next = data.documents;
      }
      onChange(next);
      setLabel("");
      setExpiry("");
      toast.success("Document ajouté");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur upload");
    } finally {
      setUploading(false);
    }
  };

  const remove = async (docId) => {
    if (!(await confirmAction({
      title: "Supprimer ce document ?",
      message: "Le document sera définitivement supprimé du dossier du véhicule.",
      confirmLabel: "Supprimer le document",
      variant: "danger",
    }))) return;
    try {
      const { data } = await api.delete(`/vendor/vehicles/${vehicleId}/documents/${docId}`);
      onChange(data.documents);
      toast.success("Document supprimé");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Erreur");
    }
  };

  const openDoc = async (doc) => {
    try {
      const token = sessionStorage.getItem("cm_token");
      const url = `${API}/vendor/vehicle-documents/${doc.path}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("failed");
      const blob = await res.blob();
      const objUrl = URL.createObjectURL(blob);
      window.open(objUrl, "_blank");
    } catch (err) {
      toast.error("Impossible d'ouvrir le document");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Upload form */}
      <Card title="Ajouter un document" className="lg:col-span-1">
        <div className="space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm bg-white"
              data-testid="doc-type"
            >
              {DOC_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Libellé (facultatif)</label>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Ex : Carte grise recto"
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              data-testid="doc-label"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Date d'expiration (facultatif)</label>
            <input
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
              data-testid="doc-expiry"
            />
          </div>
          <label className={`block rounded-xl border-2 border-dashed text-center py-6 px-4 cursor-pointer transition-colors ${uploading ? "border-violet-300 bg-violet-50" : "border-slate-200 hover:border-violet-400 hover:bg-violet-50/40"}`} data-testid="doc-upload-zone">
            {uploading ? (
              <Loader2 className="h-5 w-5 animate-spin text-violet-600 mx-auto" />
            ) : (
              <>
                <Upload className="h-5 w-5 text-slate-400 mx-auto mb-2" />
                <span className="text-xs font-medium text-slate-700">Cliquez pour ajouter</span>
                <div className="text-[10px] text-slate-400 mt-1">JPG/PNG/WebP/PDF · max 10 Mo</div>
              </>
            )}
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              multiple
              className="hidden"
              disabled={uploading}
              onChange={(e) => {
                upload(Array.from(e.target.files || []));
                e.target.value = "";
              }}
              data-testid="doc-file-input"
            />
          </label>
        </div>
      </Card>

      {/* Documents list */}
      <Card title={`Documents (${documents.length})`} className="lg:col-span-2">
        {documents.length === 0 ? (
          <p className="text-sm text-slate-500 italic py-8 text-center">Aucun document. Ajoutez la carte grise, l'assurance, le contrôle technique…</p>
        ) : (
          <div className="space-y-2">
            {documents.map((d) => {
              const typeLabel = DOC_TYPES.find((t) => t.value === d.type)?.label || d.type;
              const expired = d.expiry_date && new Date(d.expiry_date) < new Date();
              const soonExpiry = d.expiry_date && !expired && (new Date(d.expiry_date) - new Date()) / (1000 * 60 * 60 * 24) < 30;
              return (
                <div key={d.id} className="flex items-center justify-between gap-3 p-3 rounded-xl border border-slate-100 hover:border-slate-300" data-testid={`doc-${d.id}`}>
                  <div className="flex items-start gap-3 min-w-0 flex-1">
                    <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0">
                      <FileText className="h-4 w-4 text-slate-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 truncate">{d.label}</div>
                      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-semibold">
                          {typeLabel}
                        </span>
                        <span>{Math.round((d.size || 0) / 1024)} Ko</span>
                        {d.expiry_date && (
                          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                            expired ? "bg-red-100 text-red-700" :
                            soonExpiry ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"
                          }`}>
                            <Calendar className="h-2.5 w-2.5" />
                            {expired ? "Expiré" : "Expire"} le {formatDateFR(d.expiry_date)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => openDoc(d)}
                      className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-600"
                      title="Ouvrir"
                      data-testid={`open-doc-${d.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => remove(d.id)}
                      className="h-8 w-8 rounded-full hover:bg-red-50 flex items-center justify-center text-red-600"
                      title="Supprimer"
                      data-testid={`delete-doc-${d.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}

// ===== Small building blocks =====
function Card({ title, children, className = "" }) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white p-5 ${className}`}>
      <h3 className="font-heading text-sm font-semibold text-slate-900 mb-4">{title}</h3>
      {children}
    </div>
  );
}

function Row({ label, value, mono, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5 border-b border-slate-50 last:border-0">
      <span className="text-xs text-slate-500 inline-flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" />}
        {label}
      </span>
      <span className={`text-sm text-slate-900 text-right ${mono ? "font-mono" : ""}`}>{value ?? "—"}</span>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
      <div className={`mt-1 font-heading text-xl font-semibold ${accent ? "text-violet-600" : "text-slate-900"}`}>{value}</div>
    </div>
  );
}

// ===== Labels helpers =====
function condLabel(v) {
  const map = { excellent: "Excellent", good: "Bon", fair: "Moyen", poor: "Dégradé" };
  return map[v] || "—";
}

function equipLabel(k) {
  const map = {
    gps: "GPS", bluetooth: "Bluetooth", carplay: "Apple CarPlay", android_auto: "Android Auto",
    air_conditioning: "Climatisation", baby_seat: "Siège bébé", snow_chains: "Chaînes neige",
    winter_tires: "Pneus hiver", pmr: "Accessibilité PMR", pmr_ramp: "Rampe PMR",
    rear_camera: "Caméra recul", parking_sensors: "Radar recul", cruise_control: "Régulateur",
  };
  return map[k] || k;
}


function EmbedWrap({ children }) {
  return <>{children}</>;
}
